import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  CalendarDays,
  MapPin,
  Loader2,
  Ticket as TicketIcon,
  CheckCircle2,
  Download,
  Mail,
  Copy,
  Compass,
  Apple,
  Wallet,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import {
  ordersApi,
  walletApi,
  downloadAllTicketsUrl,
  ticketPdfUrl,
  formatAuDate,
  centsToAUD,
} from "../lib/events";
import { extractError } from "../lib/api";
import { Logo } from "../components/Logo";
import { ManageCookiesLink } from "../components/CookieConsent";

function TicketRow({ t, event, walletFlags, orderToken }) {
  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-[1fr_auto] border-2 border-[var(--gt-ink)] rounded-sm bg-white overflow-hidden shadow-[6px_6px_0px_0px_var(--gt-ink)]"
      data-testid={`hub-ticket-${t.code}`}
    >
      <div className="p-6">
        <div className="gt-label mb-2">Ticket</div>
        <div className="font-heading text-2xl font-black tracking-tight leading-tight mb-1">
          {t.ticket_type_name}
        </div>
        <div className="text-xs text-[var(--gt-ink-2)] mb-4">
          {event?.title} · {formatAuDate(event?.start_at, event?.timezone)}
        </div>

        {t.seat_label ? (
          <div className="mb-4 inline-flex items-center gap-2 px-2 py-1 bg-[var(--gt-ink)] text-[var(--gt-bg-marketing)] rounded-sm" data-testid={`hub-ticket-seat-${t.code}`}>
            <span className="text-[10px] uppercase tracking-widest opacity-70">Seat</span>
            <span className="font-mono font-bold text-sm">
              {t.section ? `${t.section} · ` : ""}
              {t.row_label ? `Row ${t.row_label} · ` : ""}
              {t.seat_label}
            </span>
          </div>
        ) : (
          <div className="mb-4 inline-flex items-center gap-2 px-2 py-1 border-2 border-[var(--gt-ink)] rounded-sm" data-testid={`hub-ticket-ga-${t.code}`}>
            <span className="text-[10px] uppercase tracking-widest text-[var(--gt-ink-2)]">General Admission</span>
          </div>
        )}

        {t.attendee_name && (
          <div className="mb-4" data-testid={`ticket-attendee-${t.code}`}>
            <div className="gt-label mb-1">Attendee</div>
            <div className="text-sm font-semibold">{t.attendee_name}</div>
          </div>
        )}

        <div className="gt-label mb-1">Code</div>
        <div className="font-mono text-lg font-bold" data-testid={`hub-ticket-code-${t.code}`}>{t.code}</div>
        <div className="mt-2 text-[10px] uppercase tracking-widest text-[var(--gt-ink-3)]">
          {t.price_cents === 0 ? "Complimentary · A$0.00" : centsToAUD(t.price_cents)}
        </div>

        <div className="mt-5 flex flex-wrap gap-2" data-testid={`hub-ticket-actions-${t.code}`}>
          <Link
            to={`/t/${t.code}`}
            className="gt-btn-ghost text-xs"
            data-testid={`hub-open-ticket-${t.code}`}
          >
            <TicketIcon size={12} /> Open ticket
          </Link>
          <a
            href={ticketPdfUrl(t.code)}
            target="_blank"
            rel="noreferrer"
            className="gt-btn-ghost text-xs"
            data-testid={`hub-download-pdf-${t.code}`}
          >
            <Download size={12} /> PDF
          </a>
          {walletFlags?.apple_wallet_enabled && (
            <a
              href={walletApi.applePassUrl(t.code, orderToken)}
              className="gt-btn-ghost text-xs"
              data-testid={`hub-apple-wallet-${t.code}`}
            >
              <Apple size={12} /> Apple Wallet
            </a>
          )}
          {walletFlags?.google_wallet_enabled && (
            <a
              href={walletApi.googlePassUrl(t.code, orderToken)}
              className="gt-btn-ghost text-xs"
              data-testid={`hub-google-wallet-${t.code}`}
            >
              <Wallet size={12} /> Google Wallet
            </a>
          )}
        </div>
      </div>
      <div className="bg-white border-t-2 sm:border-t-0 sm:border-l-2 border-[var(--gt-ink)] p-6 flex flex-col items-center justify-center">
        <QRCodeSVG value={t.code} size={112} bgColor="#ffffff" fgColor="#09090b" level="M" />
        <div className="mt-2 text-[10px] uppercase tracking-widest text-[var(--gt-ink-3)]">
          Scan at door
        </div>
      </div>
    </div>
  );
}

export default function OrderConfirmation() {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [resending, setResending] = useState(false);
  const [walletFlags, setWalletFlags] = useState({ apple_wallet_enabled: false, google_wallet_enabled: false });

  useEffect(() => {
    let alive = true;
    if (!token) {
      setError("Missing or invalid order access token. Please open your order from the confirmation email.");
      setLoading(false);
      return;
    }
    ordersApi
      .get(orderId, token)
      .then((o) => { if (alive) setOrder(o); })
      .catch((e) => { if (alive) setError(extractError(e)); })
      .finally(() => { if (alive) setLoading(false); });
    walletApi.config().then((c) => { if (alive) setWalletFlags(c); }).catch(() => {});
    return () => { alive = false; };
  }, [orderId, token]);

  const onResend = async () => {
    setResending(true);
    try {
      const r = await ordersApi.resend(orderId, token);
      toast.success(r.message || `Tickets re-sent to ${order?.buyer_email}`);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setResending(false);
    }
  };

  const onCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Order link copied");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="order-hub-loading">
        <Loader2 className="animate-spin text-[var(--gt-ink-2)]" size={20} />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-[var(--gt-bg-marketing)]" data-testid="order-hub-error">
        <div className="max-w-md w-full text-center">
          <div className="gt-label mb-2">Order unavailable</div>
          <h1 className="font-heading text-3xl font-black tracking-tighter mb-3">
            We couldn&apos;t open this order.
          </h1>
          <p className="text-[var(--gt-ink-2)] mb-6" data-testid="order-hub-error-msg">
            {error || "This link is missing its access token or has expired."}
          </p>
          <Link to="/resend" className="gt-btn-primary" data-testid="order-hub-resend-fallback">
            <Mail size={14} /> Resend my tickets
          </Link>
        </div>
      </div>
    );
  }

  const event = order.event || {};
  const scPaidBy = order.service_charge_paid_by || (order.fee_mode === "passed_on" ? "customer" : "included");
  const gstPaidBy = order.gst_paid_by || order.tax_paid_by || "customer";
  const gstEnabled = !(order.gst_enabled === false || order.gst_registered_snapshot === false);

  return (
    <div className="min-h-screen bg-[var(--gt-bg-marketing)]" data-testid="order-hub-page">
      <nav className="gt-glass-nav">
        <div className="max-w-4xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
          <Logo size="md" />
          <Link to="/" className="gt-btn-ghost text-sm" data-testid="order-hub-home">
            <ArrowLeft size={14} /> Home
          </Link>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-10 py-8 sm:py-14" data-testid="order-hub-main">
        {/* ---- Confirmed banner ---- */}
        <div className="mb-10 text-center">
          <CheckCircle2 size={48} className="mx-auto text-[var(--gt-accent)] mb-4" strokeWidth={1.5} />
          <div className="gt-label mb-2" data-testid="order-hub-status">Order confirmed</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mb-3">
            You&apos;re going.
          </h1>
          <p className="text-[var(--gt-ink-2)] max-w-lg mx-auto">
            {order.tickets.length} ticket{order.tickets.length === 1 ? "" : "s"} for{" "}
            <span className="font-semibold text-[var(--gt-ink)]" data-testid="order-hub-event-title">
              {event.title}
            </span>. Save this page or use the buttons below.
          </p>
        </div>

        {/* ---- Order summary card ---- */}
        <div className="gt-card mb-6" data-testid="order-hub-summary">
          <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
            <div>
              <div className="gt-label mb-1">Order number</div>
              <div className="font-mono text-sm break-all" data-testid="order-hub-id">{order.id}</div>
              <div className="gt-label mt-3 mb-1">Customer email</div>
              <div className="text-sm" data-testid="order-hub-buyer-email">{order.buyer_email}</div>
              {order.buyer_phone && (
                <>
                  <div className="gt-label mt-3 mb-1">Customer phone</div>
                  <div className="text-sm" data-testid="order-hub-buyer-phone">{order.buyer_phone}</div>
                </>
              )}
            </div>
            <div className="text-right">
              <div className="gt-label mb-1">Total paid</div>
              <div className="font-heading text-2xl font-black tracking-tight" data-testid="order-hub-total">
                {order.amount_total_cents === 0 ? "A$0.00" : centsToAUD(order.amount_total_cents)}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-t border-[var(--gt-line)] pt-4 text-sm">
            <div>
              <div className="gt-label mb-1">Event</div>
              <div className="font-semibold" data-testid="order-hub-event-name">{event.title}</div>
              <div className="text-[var(--gt-ink-2)] text-xs flex items-start gap-1.5 mt-1">
                <CalendarDays size={13} className="mt-0.5" />
                <span data-testid="order-hub-event-date">{formatAuDate(event.start_at, event.timezone)}</span>
              </div>
              <div className="text-[var(--gt-ink-2)] text-xs flex items-start gap-1.5 mt-1">
                <MapPin size={13} className="mt-0.5" />
                <span data-testid="order-hub-event-venue">
                  {event.venue_name}
                  {event.state ? `, ${event.state}` : ""}
                  {event.venue_address ? <><br /><span className="text-[var(--gt-ink-3)]">{event.venue_address}</span></> : null}
                </span>
              </div>
            </div>
            <div>
              <div className="gt-label mb-1">Ticket holder</div>
              <div className="font-semibold" data-testid="order-hub-buyer-name">{order.buyer_name}</div>
            </div>
          </div>
        </div>

        {/* ---- Action toolbar ---- */}
        <div className="flex flex-wrap items-center gap-2 mb-8" data-testid="order-hub-actions">
          <a
            href={downloadAllTicketsUrl(order.id, order.access_token || token)}
            target="_blank"
            rel="noreferrer"
            className="gt-btn-primary text-sm"
            data-testid="order-hub-download-all"
          >
            <Download size={14} /> Download all tickets
          </a>
          <button
            type="button"
            onClick={onResend}
            className="gt-btn-ghost text-sm"
            disabled={resending}
            data-testid="order-hub-resend-btn"
          >
            {resending ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
            Resend to {order.buyer_email}
          </button>
          <button
            type="button"
            onClick={onCopyLink}
            className="gt-btn-ghost text-sm"
            data-testid="order-hub-copy-link"
          >
            <Copy size={14} /> Copy link
          </button>
        </div>

        {/* ---- Tickets ---- */}
        <div className="gt-label mb-4">Your tickets</div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-10" data-testid="order-hub-tickets-grid">
          {order.tickets.map((t) => (
            <TicketRow
              key={t.id}
              t={t}
              event={event}
              walletFlags={walletFlags}
              orderToken={order.access_token || token}
            />
          ))}
        </div>

        {/* ---- Financial breakdown ---- */}
        <div className="gt-card mb-10" data-testid="order-hub-breakdown">
          <div className="gt-label mb-3">Order summary</div>
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between">
              <span>Ticket subtotal</span>
              <span className="font-mono" data-testid="hub-subtotal">
                {centsToAUD(order.gross_ticket_cents || 0)}
              </span>
            </div>
            {order.addons_total_cents > 0 && (
              <div className="flex justify-between">
                <span>Extras</span>
                <span className="font-mono" data-testid="hub-extras">
                  {centsToAUD(order.addons_total_cents)}
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span>Service fee</span>
              <span className="font-mono" data-testid="hub-service-charge">
                {scPaidBy === "customer"
                  ? centsToAUD(order.service_charge_cents ?? order.fee_cents ?? 0)
                  : "Included in ticket price"}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Service fee GST</span>
              <span className="font-mono" data-testid="hub-gst">
                {!gstEnabled
                  ? "Not applied"
                  : gstPaidBy === "customer"
                    ? centsToAUD(order.gst_amount_cents ?? order.gst_cents ?? 0)
                    : "Included in ticket price"}
              </span>
            </div>
          </div>
          <div className="mt-4 border-t border-[var(--gt-line)] pt-3 flex justify-between items-center">
            <span className="gt-label">Total paid (AUD)</span>
            <span className="font-heading text-2xl font-black tracking-tight" data-testid="hub-total-paid">
              {centsToAUD(order.amount_total_cents || 0)}
            </span>
          </div>
        </div>

        <div className="text-center">
          <Link to="/events" className="gt-btn-primary" data-testid="order-hub-browse-more">
            <Compass size={14} /> Browse more events
          </Link>
        </div>

        {order.dev_email_preview_url && (
          <div
            className="mt-6 border-2 border-dashed border-[var(--gt-ink-2)] bg-white px-4 py-3 text-xs text-[var(--gt-ink-2)] flex flex-wrap items-center justify-between gap-2"
            data-testid="hub-dev-email-preview-banner"
          >
            <span className="uppercase tracking-widest text-[10px] text-[var(--gt-ink-3)]">Dev preview</span>
            <a
              href={order.dev_email_preview_url}
              target="_blank"
              rel="noreferrer"
              className="underline underline-offset-2 font-mono break-all"
              data-testid="hub-dev-email-preview-link"
            >
              Open confirmation email
            </a>
          </div>
        )}
      </div>

      <footer className="bg-[var(--gt-ink)] text-[var(--gt-bg-marketing)] mt-16">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <Logo inverse size="md" />
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-5">
            <ManageCookiesLink className="text-xs tracking-[0.25em] uppercase text-[var(--gt-ink-3)]" />
            <div className="text-xs tracking-[0.25em] uppercase text-[var(--gt-ink-3)]">
              © {new Date().getFullYear()} Getickets · AUD · GST-Ready
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
